/**
 * Barra de cuenta regresiva (sections/countdown-bar.liquid).
 *
 * - `data-mode="date"`: cuenta hasta `data-end`; si hay `data-start`, no aparece antes de esa hora.
 * - `data-mode="minutes"`: cada comprador tiene su propia fecha límite, guardada en localStorage.
 * - Al terminar, según `data-on-end`: se oculta, se queda en 00, muestra un mensaje o reinicia.
 * - Con `data-sticky` o abajo, publica su altura en `--countdown-bar-height` para que la barra
 *   de anuncios y el header fijos se estacionen debajo (o el body deje espacio abajo).
 * - Si el cliente la cierra, no vuelve a aparecer durante la visita (sessionStorage).
 *
 * Eventos (no se envían dentro del editor de temas):
 *   countdown_bar_view  — la barra se vio al menos al 50% (una vez por carga de página)
 *   countdown_bar_click — clic en el botón o en la barra
 *   countdown_bar_close — clic en la X
 * Se publican como eventos personalizados de Shopify; para verlos en GA4 hace falta un píxel
 * personalizado en Configuración → Eventos de clientes que los reenvíe. También se mandan a
 * gtag y a window.dataLayer si existen en la página.
 */

const CLOSED_KEY = 'countdown_bar_closed';
const DEADLINE_KEY = 'countdown_bar_deadline';
const UNITS = /** @type {const} */ (['days', 'hours', 'minutes', 'seconds']);

/** @param {Storage | undefined} store */
const safeStorage = (store) => ({
  /** @param {string} key */
  get(key) {
    try {
      return store?.getItem(key) ?? null;
    } catch {
      return null;
    }
  },
  /** @param {string} key @param {string} value */
  set(key, value) {
    try {
      store?.setItem(key, value);
    } catch {
      /* modo privado o storage bloqueado */
    }
  },
  /** @param {string} key */
  remove(key) {
    try {
      store?.removeItem(key);
    } catch {
      /* idem */
    }
  },
});

const session = safeStorage(window.sessionStorage);
const local = safeStorage(window.localStorage);

/** @param {number} n */
const pad = (n) => String(n).padStart(2, '0');

class CountdownBar extends HTMLElement {
  /** @type {AbortController | undefined} */
  #abort;
  /** @type {number | undefined} */
  #interval;
  /** @type {ResizeObserver | undefined} */
  #resizeObserver;
  /** @type {IntersectionObserver | undefined} */
  #viewObserver;
  #ended = false;

  connectedCallback() {
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.digits = Object.fromEntries(
      UNITS.map((unit) => [unit, this.querySelector(`[data-unit="${unit}"]`)])
    );

    if (!this.designMode && session.get(this.#closedKey)) {
      this.#hide();
      return;
    }

    this.querySelector('[data-cb-close]')?.addEventListener('click', this.#onClose, { signal });
    this.querySelectorAll('[data-cb-click]').forEach((link) =>
      link.addEventListener('click', this.#onClick, { signal })
    );

    this.#watchHeight();
    this.#tick();
    this.#interval = window.setInterval(this.#tick, 1000);

    if (!this.designMode) this.#watchView();
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#resizeObserver?.disconnect();
    this.#viewObserver?.disconnect();
    window.clearInterval(this.#interval);
    this.#publishHeight(0);
  }

  get designMode() {
    return Boolean(window.Shopify?.designMode);
  }

  get timerName() {
    return this.dataset.name || this.dataset.sectionId || 'countdown';
  }

  get #closedKey() {
    // La fecha de fin forma parte de la clave: una campaña nueva vuelve a aparecer.
    return `${CLOSED_KEY}:${this.dataset.sectionId}:${this.dataset.end || this.dataset.minutes}`;
  }

  get #deadlineKey() {
    return `${DEADLINE_KEY}:${this.dataset.sectionId}:${this.dataset.minutes}`;
  }

  /** @returns {number | null} fecha límite en ms, o null si la configuración no es válida */
  #deadline() {
    if (this.dataset.mode === 'minutes') {
      const minutes = Number(this.dataset.minutes) || 0;
      if (minutes <= 0) return null;

      // En el editor, siempre la cuenta completa para poder ver cómo queda.
      if (this.designMode) return Date.now() + minutes * 60_000;

      const stored = Number(local.get(this.#deadlineKey));
      if (stored > 0) {
        if (stored > Date.now() || this.dataset.onEnd !== 'restart') return stored;
      }

      const fresh = Date.now() + minutes * 60_000;
      local.set(this.#deadlineKey, String(fresh));
      return fresh;
    }

    const end = Date.parse(this.dataset.end ?? '');
    return Number.isNaN(end) ? null : end;
  }

  #tick = () => {
    const deadline = this.#deadline();

    if (deadline === null) {
      // Fecha mal escrita: en la tienda no se muestra; en el editor se ve en 00 para corregirla.
      if (this.designMode) this.#render(0);
      else this.#hide();
      return;
    }

    const start = Date.parse(this.dataset.start ?? '');
    if (!this.designMode && !Number.isNaN(start) && Date.now() < start) {
      this.#hide({ keepTicking: true });
      return;
    }
    this.#show();

    const remaining = Math.max(0, Math.floor((deadline - Date.now()) / 1000));
    this.#render(remaining);

    if (remaining === 0) this.#onEnd();
  };

  /** @param {number} total segundos restantes */
  #render(total) {
    const values = {
      days: Math.floor(total / 86400),
      hours: Math.floor((total % 86400) / 3600),
      minutes: Math.floor((total % 3600) / 60),
      seconds: total % 60,
    };

    for (const unit of UNITS) {
      const el = this.digits?.[unit];
      const value = el?.querySelector('[data-value]');
      if (value) value.textContent = pad(values[unit]);
    }

    const days = this.digits?.days;
    if (days) {
      const hideDays = this.hasAttribute('data-hide-zero-days') && values.days === 0;
      days.hidden = hideDays;
      const sep = days.nextElementSibling;
      if (sep instanceof HTMLElement && sep.matches('.countdown-bar__sep')) sep.hidden = hideDays;
    }
  }

  #onEnd() {
    if (this.#ended) return;

    const action = this.dataset.onEnd;

    if (action === 'restart' && this.dataset.mode === 'minutes') {
      local.remove(this.#deadlineKey);
      return;
    }

    // En el editor no se oculta nada: la barra tiene que seguir siendo editable.
    if (this.designMode) return;

    this.#ended = true;
    window.clearInterval(this.#interval);

    if (action === 'hide') {
      this.#hide();
    } else if (action === 'message') {
      const message = this.querySelector('[data-ended]');
      const timer = this.querySelector('.countdown-bar__timer');
      if (message instanceof HTMLElement && timer instanceof HTMLElement) {
        timer.hidden = true;
        message.hidden = false;
      }
    }
  }

  #show() {
    if (!this.hidden) return;
    this.hidden = false;
    this.#publishHeight(this.offsetHeight);
  }

  /** @param {{ keepTicking?: boolean }} [options] */
  #hide({ keepTicking = false } = {}) {
    this.hidden = true;
    if (!keepTicking) window.clearInterval(this.#interval);
    this.#publishHeight(0);
  }

  get #needsHeight() {
    return this.hasAttribute('data-sticky') || this.dataset.position === 'bottom';
  }

  #watchHeight() {
    if (!this.#needsHeight) return;

    this.#resizeObserver = new ResizeObserver(() => {
      if (this.hidden) return;
      this.#publishHeight(this.offsetHeight);
    });
    this.#resizeObserver.observe(this);
    this.#stickySiblings.forEach((section) => this.#resizeObserver?.observe(section));
  }

  /**
   * Secciones del grupo header que vienen después de esta y antes del header. En thebox,
   * assets/custom.css las fija arriba (la barra de anuncios), así que el header debe
   * estacionarse debajo de todas ellas y no solo de la cuenta regresiva.
   * @returns {HTMLElement[]}
   */
  get #stickySiblings() {
    if (!this.hasAttribute('data-sticky')) return [];

    /** @type {HTMLElement[]} */
    const sections = [];
    let node = this.closest('.shopify-section')?.nextElementSibling;
    while (node instanceof HTMLElement && !node.classList.contains('header-section')) {
      if (node.classList.contains('shopify-section-group-header-group')) sections.push(node);
      node = node.nextElementSibling;
    }
    return sections;
  }

  /** @param {number} height */
  #publishHeight(height) {
    if (!this.#needsHeight) return;

    const siblings = height > 0 ? this.#stickySiblings.reduce((sum, el) => sum + el.offsetHeight, 0) : 0;
    // Hacia abajo: redondear hacia arriba deja una rendija bajo la barra al hacer scroll.
    const value = `${Math.floor(height)}px`;
    const stack = `${Math.floor(height + siblings)}px`;
    const { style } = document.body;
    if (style.getPropertyValue('--countdown-bar-height') === value &&
        style.getPropertyValue('--countdown-stack-height') === stack) return;
    style.setProperty('--countdown-bar-height', value);
    style.setProperty('--countdown-stack-height', stack);

    // El header fijo lee su `top` para detectar cuándo está pegado; hay que avisarle.
    requestAnimationFrame(() => {
      const header = document.querySelector('header-component');
      if (header && 'refreshStickyOffset' in header && typeof header.refreshStickyOffset === 'function') {
        header.refreshStickyOffset();
      }
    });
  }

  #watchView() {
    this.#viewObserver = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting) || this.hidden) return;
        this.#track('countdown_bar_view');
        this.#viewObserver?.disconnect();
      },
      { threshold: 0.5 }
    );
    this.#viewObserver.observe(this);
  }

  #onClick = () => {
    if (!this.designMode) this.#track('countdown_bar_click');
  };

  #onClose = () => {
    if (!this.designMode) {
      this.#track('countdown_bar_close');
      session.set(this.#closedKey, '1');
    }
    this.#hide();
  };

  /** @param {string} event */
  #track(event) {
    const link = /** @type {HTMLAnchorElement | null} */ (this.querySelector('[data-cb-click]'));
    const payload = {
      timer_name: this.timerName,
      timer_type: this.dataset.mode,
      page_path: window.location.pathname,
      link_url: link?.href ?? '',
    };

    try {
      window.Shopify?.analytics?.publish?.(event, payload);
    } catch {
      /* Customer events no disponible */
    }

    if (typeof window.gtag === 'function') {
      window.gtag('event', event, payload);
    }

    if (Array.isArray(window.dataLayer)) {
      window.dataLayer.push({ event, ...payload });
    }
  }
}

if (!customElements.get('countdown-bar')) {
  customElements.define('countdown-bar', CountdownBar);
}
